import type { FormEvent, JSX } from 'react';
import { useState, useEffect, useRef } from 'react';
import { track } from '@databuddy/sdk';
import { PageLayout, ChildrenSection, StrategyCard } from '@/components';
import {
  CompanyFormSection,
  VATFormSection,
  ShareholderFormSection,
} from '@/components/contractor';
import { FeatureHighlights, FAQSection, FAQSchema } from '@/components/seo';
import type { FAQItem } from '@/components/seo';
import { FormCard, Alert, Tabs, Card, CollapsibleSection, Checkbox } from '@/components/ui';
import { useSession } from '@/contexts/AuthContext';
import { useContractorReport } from '@/hooks/useContractorReport';
import { useContractorState, DEFAULT_PARTNER } from '@/hooks/useContractorState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useContractorQueryParams } from '@/hooks/useQueryParamsInit';
import { INDEXABLE_ROUTE_META } from '@/seo/routeMeta';

const CONTRACTOR_FAQS: FAQItem[] = [
  {
    question: 'How much will I take home as a UK contractor?',
    answer:
      'Enter your annual revenue and expenses above to see your complete take-home calculation. We show Corporation Tax, director\'s salary, dividends, and personal tax in one clear breakdown with visual charts.',
  },
  {
    question: "What is the most tax-efficient director's salary for 2026/27?",
    answer:
      'For most directors, the optimal salary is at or just above the National Insurance threshold (currently £12,570). This maximizes tax efficiency while maintaining NI contribution credits. Our calculator shows the exact optimal split for your situation.',
  },
  {
    question: 'Should I pay myself salary or dividends?',
    answer:
      "It depends on your total income and company profits. Dividends are taxed at lower rates than salary but don't qualify for NI credits. Our calculator compares different salary/dividend splits side-by-side so you can see exactly which approach gives you the most cash in hand.",
  },
  {
    question: 'How does IR35 affect my take-home pay?',
    answer:
      "If you're inside IR35, you're taxed as an employee — meaning PAYE and NI apply to your earnings. Our calculator lets you compare inside vs outside IR35 scenarios to see the real difference in your take-home pay.",
  },
];

export default function ContractorPage(): JSX.Element {
  useDocumentMeta(INDEXABLE_ROUTE_META['/contractor']);

  const { user } = useSession();
  const contractorState = useContractorState();
  const { report, loading, error, submit } = useContractorReport();
  const [activeTab, setActiveTab] = useState<'input' | 'strategies'>('input');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Parse and apply query parameters (highest priority - runs once on mount)
  const queryParams = useContractorQueryParams();
  const hasAppliedQueryParams = useRef(false);

  // Destructure setters and values to avoid effect re-running on every render
  // (contractorState returns a new object each render)
  const {
    director,
    setAnnualRevenue,
    setBusinessExpenses,
    setRetainedProfits,
    setTotalPensionContribution,
    setEligibleForEmploymentAllowance,
    setAssociatedCompanies,
    setVatRegistered,
    setVatUsingFlatRateScheme,
    setVatFlatRatePercentage,
    setDirector,
    setPartner,
    setIncludePartner,
    setChildren,
    setClaimsChildBenefit,
  } = contractorState;

  useEffect(() => {
    if (hasAppliedQueryParams.current) return;
    hasAppliedQueryParams.current = true;

    // Apply company fields
    if (queryParams.annualRevenue !== undefined) {
      setAnnualRevenue(queryParams.annualRevenue);
    }
    if (queryParams.businessExpenses !== undefined) {
      setBusinessExpenses(queryParams.businessExpenses);
    }
    if (queryParams.retainedProfits !== undefined) {
      setRetainedProfits(queryParams.retainedProfits);
    }
    if (queryParams.totalPensionContribution !== undefined) {
      setTotalPensionContribution(queryParams.totalPensionContribution);
    }
    if (queryParams.eligibleForEmploymentAllowance !== undefined) {
      setEligibleForEmploymentAllowance(queryParams.eligibleForEmploymentAllowance);
    }
    if (queryParams.associatedCompanies !== undefined) {
      setAssociatedCompanies(queryParams.associatedCompanies);
    }

    // Apply VAT fields
    if (queryParams.vatRegistered !== undefined) {
      setVatRegistered(queryParams.vatRegistered);
    }
    if (queryParams.vatUsingFlatRateScheme !== undefined) {
      setVatUsingFlatRateScheme(queryParams.vatUsingFlatRateScheme);
    }
    if (queryParams.vatFlatRatePercentage !== undefined) {
      setVatFlatRatePercentage(queryParams.vatFlatRatePercentage);
    }

    // Apply director fields (setDirector doesn't support functional updates)
    if (queryParams.director && Object.keys(queryParams.director).length > 0) {
      setDirector({ ...director, ...queryParams.director });
    }

    // Apply partner fields first (before setting includePartner, since state updates are async)
    // Use DEFAULT_PARTNER as base since contractorState.partner may still be null
    if (queryParams.partner && Object.keys(queryParams.partner).length > 0) {
      setPartner({ ...DEFAULT_PARTNER, ...queryParams.partner });
    }

    // Apply partner inclusion
    if (queryParams.includePartner !== undefined) {
      setIncludePartner(queryParams.includePartner);
    }

    // Apply children
    if (queryParams.children && queryParams.children.length > 0) {
      setChildren(queryParams.children);
    }

    // Apply child benefit
    if (queryParams.claimsChildBenefit !== undefined) {
      setClaimsChildBenefit(queryParams.claimsChildBenefit);
    }
  }, [
    queryParams,
    director,
    setAnnualRevenue,
    setBusinessExpenses,
    setRetainedProfits,
    setTotalPensionContribution,
    setEligibleForEmploymentAllowance,
    setAssociatedCompanies,
    setVatRegistered,
    setVatUsingFlatRateScheme,
    setVatFlatRatePercentage,
    setDirector,
    setPartner,
    setIncludePartner,
    setChildren,
    setClaimsChildBenefit,
  ]);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const input = contractorState.getContractorInput();
    track('calculation_attempted', { page: 'contractor', source: 'manual' });
    const result = await submit(input);
    if (result.success) {
      track('calculation_succeeded', { page: 'contractor' });
    } else {
      track('calculation_failed', {
        page: 'contractor',
        reason: result.error ?? 'unknown',
      });
    }
    if (isMobile && result.success) {
      setActiveTab('strategies');
    }
  };

  const tabs = [
    { id: 'input' as const, label: 'Input' },
    { id: 'strategies' as const, label: 'Strategies' },
  ];

  return (
    <PageLayout maxWidth="full">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-4">UK Contractor Tax Calculator 2026/27</h1>

        {/* Mobile tabs */}
        <div className="md:hidden mb-4">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id as 'input' | 'strategies')} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left column: Form inputs */}
          <div
            className={`lg:col-span-4 ${
              isMobile && activeTab !== 'input' ? 'hidden' : ''
            }`}
          >
            <FormCard
              title="Company & Director Details"
              buttonText="Calculate"
              onSubmit={handleSubmit}
              loading={loading}
            >
              <div className="space-y-6">
                {/* Company Section */}
                <div>
                  <h3 className="font-semibold text-lg mb-4">Company Financials</h3>
                  <CompanyFormSection state={contractorState} />
                </div>

                {/* VAT Section */}
                <CollapsibleSection title="VAT Settings">
                  <VATFormSection state={contractorState} />
                </CollapsibleSection>

                {/* Director Section */}
                <ShareholderFormSection
                  role="Director"
                  data={contractorState.director}
                  setData={contractorState.setDirector}
                  showAdditionalIncome
                />

                {/* Partner Section */}
                <CollapsibleSection title="Partner / Spouse (Optional)">
                  <div className="space-y-4">
                    <Checkbox
                      checked={contractorState.includePartner}
                      onChange={(e) => contractorState.setIncludePartner(e.target.checked)}
                      label="Include partner/spouse as shareholder"
                      helperText="Partner must genuinely work in the business or have invested capital"
                    />

                    {contractorState.includePartner && contractorState.partner && (
                      <ShareholderFormSection
                        role="Partner"
                        data={contractorState.partner}
                        setData={contractorState.setPartner}
                      />
                    )}
                  </div>
                </CollapsibleSection>

                {/* Children Section */}
                <CollapsibleSection title="Children & Benefits (Optional)">
                  <div className="space-y-4">
                    <ChildrenSection
                      childrenData={contractorState.children}
                      onAdd={() =>
                        contractorState.setChildren([...contractorState.children, { age: 0 }])
                      }
                      onRemove={(idx) =>
                        contractorState.setChildren(
                          contractorState.children.filter((_, i) => i !== idx)
                        )
                      }
                      onChange={(idx, age) =>
                        contractorState.setChildren(
                          contractorState.children.map((child, i) =>
                            i === idx ? { age } : child
                          )
                        )
                      }
                    />
                    <Checkbox
                      checked={contractorState.claimsChildBenefit}
                      onChange={(e) => contractorState.setClaimsChildBenefit(e.target.checked)}
                      label="Claims Child Benefit"
                      helperText="High Income Child Benefit Charge applies if income > £60,000"
                    />
                  </div>
                </CollapsibleSection>
              </div>
            </FormCard>
          </div>

          {/* Right column: Results */}
          <div
            className={`lg:col-span-8 ${
              isMobile && activeTab !== 'strategies' ? 'hidden' : ''
            }`}
          >
            {error && (
              <Alert variant="error" title="Calculation Error">
                {error}
              </Alert>
            )}

            {loading && (
              <Card padding="lg" className="text-center">
                <div className="animate-pulse">Calculating...</div>
              </Card>
            )}

            {!loading && !error && report && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold">Tax Optimisation Strategies</h2>

                {report.strategies.length === 0 ? (
                  <Card padding="lg">
                    <p className="text-gray-600">
                      No optimisation strategies available for your current setup.
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {report.strategies.map((strategy) => (
                      <StrategyCard
                        key={strategy.name}
                        strategy={strategy}
                        onSelect={
                          user
                            ? () => {
                                // TODO: Save strategy selection
                              }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {!loading && !error && !report && (
              <Card padding="lg" className="text-center text-gray-500">
                Enter your details and click Calculate to see available tax optimisation strategies
              </Card>
            )}
          </div>
        </div>

        <div className="mt-8">
          <FeatureHighlights
            features={[
              'Calculate limited company take-home pay',
              'Compare salary vs dividend splits',
              'See the most tax-efficient director salary',
              'IR35-friendly calculations',
              'Include partner as shareholder',
            ]}
          />
        </div>

        <FAQSection items={CONTRACTOR_FAQS} />
        <FAQSchema items={CONTRACTOR_FAQS} />
      </div>
    </PageLayout>
  );
}
